import type { ChangeEvent, FC } from 'react'

import { Input, Select } from '@skytech/ui'
import { DECIMAL_PATTERN, MAX_DECIMAL_12_4, MIN_PRICE, trimLeadingZeros } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'

import type { FormFields } from '../AssetDetails/types'

import adminResources from '../../resources'

export const Hardware: FC = () => {
  const translate = useTranslation()
  const profile = useProfile()

  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  const handleCostChange = (e: ChangeEvent<HTMLInputElement>, form: FormikProps<FormFields>) => {
    let newValue = e.target.value.trim()
    if (!DECIMAL_PATTERN.test(newValue) && newValue !== '') return
    form.setFieldValue('cost', newValue)
  }

  return (
    <div className="wrap flex flex-col md:flex-row">
      <div className="mx-2 flex flex-1 basis-0 flex-col rounded-lg md:basis-[30rem]">
        <Field name="typeId">
          {({ field, form }: FieldProps) => (
            <Select
              capitalizeLabel
              id="typeId"
              label={
                <>
                  <Translation id="Type" />*
                </>
              }
              options={
                assetTypes?.types.map(({ id, type }) => ({
                  value: id,
                  label: translate(`asset_type/${type}`),
                })) || []
              }
              marginBottom={16}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>

        <Field name="assetDescription">
          {({ field, meta }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="assetDescription"
              className="description-input"
              ariaLabel={translate('Description')}
              label={
                <>
                  <Translation id="Description" />*
                </>
              }
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
            />
          )}
        </Field>
      </div>

      <div className="mx-2 flex flex-1 basis-0 flex-col rounded-lg md:basis-[30rem]">
        <div className="wrap items-between flex flex-col justify-between gap-0 md:flex-row md:gap-5">
          <div className="flex flex-1">
            <Field name="imeiNumber">
              {({ field, meta }: FieldProps) => (
                <Input
                  capitalizeLabel
                  isFullWidth
                  id="imeiNumber"
                  ariaLabel={translate('IMEI')}
                  label={<Translation id="IMEI" />}
                  wrapperClassName="w-full"
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                />
              )}
            </Field>
          </div>
          <div className="flex flex-1">
            <Field name="serialNumber">
              {({ field, meta }: FieldProps) => (
                <Input
                  capitalizeLabel
                  isFullWidth
                  id="serialNumber"
                  ariaLabel={translate('Serialnbr')}
                  label={<Translation id="Serialnbr" />}
                  wrapperClassName="w-full"
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                />
              )}
            </Field>
          </div>
        </div>
        <Field name="cost">
          {({ field, meta, form }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="cost"
              type="text"
              inputMode="decimal"
              min={String(MIN_PRICE)}
              max={String(MAX_DECIMAL_12_4)}
              LeftElement={<span>{profile?.currency}</span>}
              ariaLabel={translate('Cost')}
              label={
                <>
                  <Translation id="Cost" />*
                </>
              }
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
              value={trimLeadingZeros(field.value)}
              onChange={e => handleCostChange(e, form)}
            />
          )}
        </Field>
      </div>
    </div>
  )
}
